import type { ARCPattern, ARCRunResult, LiveSnapshot } from "@/arc/arc.types";
import { blockedJobsPattern } from "@/arc/patterns/blocked-jobs.pattern";
import { revenueAtRiskPattern } from "@/arc/patterns/revenue-at-risk.pattern";
import { permitDelaysPattern } from "@/arc/patterns/permit-delays.pattern";
import { qaFailuresPattern } from "@/arc/patterns/qa-failures.pattern";
import { collectionsOverduePattern } from "@/arc/patterns/collections-overdue.pattern";

// ── Registered patterns — order determines display priority ──────────────────
const PATTERNS: ARCPattern[] = [
  blockedJobsPattern,
  permitDelaysPattern,
  qaFailuresPattern,
  revenueAtRiskPattern,
  collectionsOverduePattern,
];

/** Exported for architecture health checks — lets moduleOwnership verify all patterns are present. */
export const REGISTERED_PATTERN_IDS: string[] = PATTERNS.map(p => p.id);

/**
 * Run all ARC patterns against a live snapshot.
 * Returns one result per pattern, regardless of whether it triggered.
 * Callers filter by `result.triggered` for display.
 */
export function runARC(snapshot: LiveSnapshot): ARCRunResult[] {
  return PATTERNS.map((pattern) => {
    try {
      const result = pattern.evaluate(snapshot);
      return {
        patternId: pattern.id,
        patternLabel: pattern.label,
        owner: pattern.owner,
        urgency: pattern.urgency,
        result,
        ranAt: snapshot.timestamp,
      };
    } catch (err) {
      console.warn(`[ARC] Pattern "${pattern.id}" threw during evaluation:`, err);
      return {
        patternId: pattern.id,
        patternLabel: pattern.label,
        owner: pattern.owner,
        urgency: pattern.urgency,
        result: {
          triggered: false,
          count: 0,
          affectedJobIds: [],
          amountAtRisk: 0,
          context: "Pattern evaluation failed",
          suggestedAction: "",
          snapshot: {},
        },
        ranAt: snapshot.timestamp,
      };
    }
  });
}

/** Returns only the triggered results, sorted by urgency then amount at risk */
export function getTriggeredResults(results: ARCRunResult[]): ARCRunResult[] {
  const urgencyOrder = { high: 0, medium: 1, low: 2 };
  return results
    .filter((r) => r.result.triggered)
    .sort((a, b) => {
      const urgencyDiff = urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      if (urgencyDiff !== 0) return urgencyDiff;
      return b.result.amountAtRisk - a.result.amountAtRisk;
    });
}
